/**
 * Owner: Masa Bayu
 * File: reader-profile-labels.ts
 * Date:Time: 2026-10-19 18:45
 * Description: §393 — label dua bahasa (en/ms) untuk skrin Profil Reader.
 */

import type { MarketplaceLanguage } from '../home/marketplace/marketplace-locale';
import type { LandingIconName } from '../home/marketplace/landing/LandingIcons';
import type { ReaderProfileSection } from './reader-profile-data';

export interface ReaderProfileLabels {
  readonly title: string;
  readonly ariaNav: string;
  readonly sections: Readonly<Record<ReaderProfileSection, string>>;
  readonly sectionIcons: Readonly<Record<ReaderProfileSection, LandingIconName>>;
  readonly badgeReader: string;
  readonly badgePublisher: string;
  readonly badgeAdmin: string;
  readonly signOut: string;
  /** Seksyen Penerbitan — angka daripada `GET /v1/publisher/dashboard`, tiada rekaan. */
  readonly publishing: Readonly<{
    heading: string;
    loading: string;
    notPublisherTitle: string;
    notPublisherBody: string;
    becomeCta: string;
    becoming: string;
    retry: string;
    kpiBooks: string;
    kpiPaidOrders: string;
    kpiPending: string;
    kpiBuyers: string;
    kpiRevenue: string;
    booksHeading: string;
    ordersHeading: string;
    colBook: string;
    colRevision: string;
    colPrice: string;
    colSold: string;
    colRevenue: string;
    listed: string;
    unlisted: string;
    emptyBooks: string;
    emptyOrders: string;
    studioCta: string;
    priceUnknown: string;
  }>;
  readonly stats: Readonly<{
    owned: string;
    reading: string;
    completed: string;
    wishlist: string;
    bookmarks: string;
    highlights: string;
    follows: string;
  }>;
  readonly continueReading: string;
  readonly seeAllLibrary: string;
  readonly notifications: string;
  readonly noNotifications: string;
  readonly markAllRead: string;
  readonly shelfTitle: string;
  readonly completedTitle: string;
  readonly wishlistTitle: string;
  readonly emptyLibrary: string;
  /** §459 — "Continue reading" kosong ≠ perpustakaan kosong; buku yang dibeli
   *  tetapi belum dibuka tiada dalam senarai ini, jadi mesej khusus diperlukan. */
  readonly emptyContinueReading: string;
  readonly openReader: string;
  readonly openDetail: string;
  readonly offlineReady: string;
  readonly ordersTitle: string;
  readonly ordersHint: string;
  readonly fullHistory: string;
  readonly license: string;
  readonly settingsLanguage: string;
  /** §418 — opyen auto pada pemilih bahasa dunia + nota fallback jujur. */
  readonly languageAuto: string;
  readonly languagePending: string;
  readonly settingsCurrency: string;
  readonly localeAuto: string;
  readonly settingsReader: string;
  readonly readerTheme: string;
  readonly readerFont: string;
  readonly readerScale: string;
  readonly readerSpacing: string;
  readonly themeLight: string;
  readonly themeSepia: string;
  readonly themeDark: string;
  readonly fontSerif: string;
  readonly fontSans: string;
  readonly openBookEnvironment: string;
  readonly settingsPayment: string;
  readonly paymentDemo: string;
  readonly settingsDevices: string;
  readonly thisDevice: string;
  readonly syncNote: string;
  /** §457 F6-C — zon "Batalkan akaun" (§453); mesej ralat datang daripada hub. */
  readonly cancelHeading: string;
  readonly cancelIntro: string;
  readonly cancelOrdersNote: string;
  readonly cancelButton: string;
  readonly cancelPasswordLabel: string;
  readonly cancelConfirm: string;
  readonly cancelDismiss: string;
  readonly cancelWorking: string;
  readonly cancelNoSession: string;
  readonly cancelNoPassword: string;
}

export const READER_PROFILE_LABELS: Readonly<Record<MarketplaceLanguage, ReaderProfileLabels>> =
  Object.freeze({
    en: Object.freeze({
      title: 'My Profile',
      ariaNav: 'Profile sections',
      sections: Object.freeze({
        overview: 'Overview',
        library: 'My Library',
        orders: 'Orders & Licenses',
        publishing: 'Publishing',
        settings: 'Settings',
      }),
      sectionIcons: Object.freeze({
        overview: 'home',
        library: 'book',
        orders: 'receipt',
        publishing: 'pen',
        settings: 'gear',
      }),
      badgeReader: 'Reader',
      badgePublisher: 'Publisher',
      badgeAdmin: 'Admin',
      signOut: 'Sign out',
      publishing: Object.freeze({
        heading: 'Publishing',
        loading: 'Loading your publishing numbers…',
        notPublisherTitle: 'This account can read but not publish yet',
        notPublisherBody:
          'Publishing turns this same account into a publisher. No new email and no second sign-in.',
        becomeCta: 'Become a publisher on this account',
        becoming: 'Upgrading…',
        retry: 'Try again',
        kpiBooks: 'Books published',
        kpiPaidOrders: 'Paid orders',
        kpiPending: 'Pending orders',
        kpiBuyers: 'Buyers',
        kpiRevenue: 'Revenue',
        booksHeading: 'Your books',
        ordersHeading: 'Recent orders',
        colBook: 'Book',
        colRevision: 'Revision',
        colPrice: 'Price',
        colSold: 'Sold',
        colRevenue: 'Revenue',
        listed: 'Listed',
        unlisted: 'Not listed',
        emptyBooks: 'Nothing published from this account yet.',
        emptyOrders: 'No orders yet.',
        studioCta: 'Open Studio',
        priceUnknown: 'Price not set',
      }),
      stats: Object.freeze({
        owned: 'Books owned',
        reading: 'Reading now',
        completed: 'Completed',
        wishlist: 'Wishlist',
        bookmarks: 'Bookmarks',
        highlights: 'Highlights',
        follows: 'Authors followed',
      }),
      continueReading: 'Continue reading',
      seeAllLibrary: 'See all library',
      notifications: 'Notifications',
      noNotifications: 'No notifications yet.',
      markAllRead: 'Mark all read',
      shelfTitle: 'My shelf',
      completedTitle: 'Completed',
      wishlistTitle: 'Wishlist',
      emptyLibrary: 'Your library is empty — find your next read in the marketplace.',
      emptyContinueReading:
        'Nothing in progress yet — open a book from your library and it will continue here.',
      openReader: 'Read now',
      openDetail: 'View',
      offlineReady: 'Offline ready',
      ordersTitle: 'Licenses',
      ordersHint: 'Every purchase is a perpetual license bound to your account.',
      fullHistory: 'Full order history',
      license: 'License',
      settingsLanguage: 'Language',
      languageAuto: 'Auto (detect from device)',
      languagePending:
        '{name} is not certified for interface text yet — dates and numbers follow it, menus stay English. Currency always follows your region.',
      settingsCurrency: 'Currency',
      localeAuto: 'Language is your choice and saved on this device; currency follows your region.',
      settingsReader: 'Reading preferences',
      readerTheme: 'Theme',
      readerFont: 'Font',
      readerScale: 'Text size',
      readerSpacing: 'Line spacing',
      themeLight: 'Light',
      themeSepia: 'Sepia',
      themeDark: 'Dark',
      fontSerif: 'Serif',
      fontSans: 'Sans',
      openBookEnvironment: 'Open in Book Environment',
      settingsPayment: 'Payment methods',
      paymentDemo: 'DEMO — card management activates with live payment provider.',
      settingsDevices: 'Devices & sync',
      thisDevice: 'This device',
      syncNote: 'Reading position syncs to your account when you open a book.',
      cancelHeading: 'Cancel account',
      cancelIntro:
        'Permanently deletes your QXRead account and the reading rights attached to it. This cannot be undone.',
      cancelOrdersNote: 'Order records are kept for accounting; your saved sessions are closed.',
      cancelButton: 'Cancel account',
      cancelPasswordLabel: 'Confirm your password',
      cancelConfirm: 'Cancel my account',
      cancelDismiss: 'Keep my account',
      cancelWorking: 'Cancelling…',
      cancelNoSession: 'Sign in with this account to cancel it.',
      cancelNoPassword:
        'Cancellation needs a password. Accounts created with Google only cannot be cancelled yet — this is a server-side gap, not a setting you can change here.',
    }),
    ms: Object.freeze({
      title: 'Profil Saya',
      ariaNav: 'Seksyen profil',
      sections: Object.freeze({
        overview: 'Ringkasan',
        library: 'Perpustakaan Saya',
        orders: 'Pesanan & Lesen',
        publishing: 'Penerbitan',
        settings: 'Tetapan',
      }),
      sectionIcons: Object.freeze({
        overview: 'home',
        library: 'book',
        orders: 'receipt',
        publishing: 'pen',
        settings: 'gear',
      }),
      badgeReader: 'Pembaca',
      badgePublisher: 'Penerbit',
      badgeAdmin: 'Pentadbir',
      signOut: 'Log keluar',
      publishing: Object.freeze({
        heading: 'Penerbitan',
        loading: 'Sedang memuat angka penerbitan anda…',
        notPublisherTitle: 'Akaun ini boleh membaca, tetapi belum boleh menerbit',
        notPublisherBody:
          'Penerbitan menukarkan akaun yang sama menjadi penerbit. Tiada e-mel baharu dan tiada log masuk kedua.',
        becomeCta: 'Jadi penerbit pada akaun ini',
        becoming: 'Sedang menaik taraf…',
        retry: 'Cuba lagi',
        kpiBooks: 'Buku diterbitkan',
        kpiPaidOrders: 'Pesanan dibayar',
        kpiPending: 'Pesanan menunggu',
        kpiBuyers: 'Pembeli',
        kpiRevenue: 'Hasil',
        booksHeading: 'Buku anda',
        ordersHeading: 'Pesanan terkini',
        colBook: 'Buku',
        colRevision: 'Semakan',
        colPrice: 'Harga',
        colSold: 'Terjual',
        colRevenue: 'Hasil',
        listed: 'Ditayangkan',
        unlisted: 'Belum ditayangkan',
        emptyBooks: 'Belum ada buku diterbitkan dari akaun ini.',
        emptyOrders: 'Belum ada pesanan.',
        studioCta: 'Buka Studio',
        priceUnknown: 'Harga belum ditetapkan',
      }),
      stats: Object.freeze({
        owned: 'Buku dimiliki',
        reading: 'Sedang baca',
        completed: 'Selesai',
        wishlist: 'Senarai henti',
        bookmarks: 'Tanda halaman',
        highlights: 'Sorotan',
        follows: 'Penulis diikuti',
      }),
      continueReading: 'Teruskan membaca',
      seeAllLibrary: 'Lihat semua perpustakaan',
      notifications: 'Notifikasi',
      noNotifications: 'Belum ada notifikasi.',
      markAllRead: 'Tandai semua dibaca',
      shelfTitle: 'Rak saya',
      completedTitle: 'Selesai dibaca',
      wishlistTitle: 'Senarai henti',
      emptyLibrary: 'Perpustakaan anda masih kosong — cari bacaan seterusnya di marketplace.',
      emptyContinueReading:
        'Belum ada bacaan berjalan — buka sebuah buku dari perpustakaan anda dan ia akan bersambung di sini.',
      openReader: 'Baca sekarang',
      openDetail: 'Lihat',
      offlineReady: 'Sedia luar talian',
      ordersTitle: 'Lesen',
      ordersHint: 'Setiap pembelian ialah lesen kekal yang terikat dengan akaun anda.',
      fullHistory: 'Sejarah pesanan penuh',
      license: 'Lesen',
      settingsLanguage: 'Bahasa',
      languageAuto: 'Auto (ikesan dari peranti)',
      languagePending:
        '{name} belum disahkan untuk teks antara muka — tarikh dan nombor ikut bahasa ini, menu kekal English. Mata wang sentiasa ikut negara anda.',
      settingsCurrency: 'Mata wang',
      localeAuto: 'Bahasa pilihan anda, disimpan pada peranti ini; mata wang ikut negara anda.',
      settingsReader: 'Pilihan bacaan',
      readerTheme: 'Tema',
      readerFont: 'Font',
      readerScale: 'Saiz teks',
      readerSpacing: 'Jarak baris',
      themeLight: 'Cerah',
      themeSepia: 'Sepia',
      themeDark: 'Gelap',
      fontSerif: 'Serif',
      fontSans: 'Sans',
      openBookEnvironment: 'Buka di Lingkungan Buku',
      settingsPayment: 'Kaedah pembayaran',
      paymentDemo: 'DEMO — pengurusan kad aktif bersama provider pembayaran sebenar.',
      settingsDevices: 'Peranti & penyegerakan',
      thisDevice: 'Peranti ini',
      syncNote: 'Posisi bacaan disegerak ke akaun anda apabila buku dibuka.',
      cancelHeading: 'Batalkan akaun',
      cancelIntro:
        'Memadam akaun QXRead anda secara kekal bersama hak bacaan yang berkaitan. Tindakan ini tidak boleh dibatalkan semula.',
      cancelOrdersNote: 'Rekod pesanan dikekalkan untuk perakaunan; sesi tersimpan anda ditutup.',
      cancelButton: 'Batalkan akaun',
      cancelPasswordLabel: 'Sahkan kata laluan anda',
      cancelConfirm: 'Batalkan akaun saya',
      cancelDismiss: 'Kekalkan akaun saya',
      cancelWorking: 'Sedang membatalkan…',
      cancelNoSession: 'Log masuk ke akaun ini untuk membatalkannya.',
      cancelNoPassword:
        'Pembatalan memerlukan kata laluan. Akaun yang dibuka hanya dengan Google belum boleh dibatalkan — ini jurang di pelayan, bukan tetapan yang boleh diubah di sini.',
    }),
  });
